import type { ExportQuality } from '@shared/plans';
import { el, clear, setClass } from './dom';
import { icon } from './Icons';
import type { ApiKeyStore } from '@/capture/ApiKeyStore';
import { looksLikeApiKey } from '@/capture/ApiKeyStore';
import { EnhanceError, enhanceRender, ENHANCE_MODEL } from '@/capture/EnhanceClient';
import { buildEnhancePrompt } from '@/capture/PromptBuilder';
import { collectRenderReferences } from '@/capture/RenderReferences';
import type { SemanticTag } from '@/semantics/SemanticTag';
import { tagImageToBlob } from './ImageIntake';
import {
  DEFAULT_STYLE_PRESET_ID,
  STYLE_PRESETS,
  getStylePreset,
  type StylePreset,
} from '@/capture/StylePresets';
import { describeExport, isCapped, toExportBlob } from '@/capture/ExportQuality';
import {
  RENDER_QUALITY_TIERS,
  estimateCostUsd,
  formatCostUsd,
  type ImageSize,
  type RenderQualityTier,
} from '@/capture/ImageSpec';
import type { NewVariant, StoredShot } from '@/capture/PhotoStorage';

/**
 * The Enhance panel: choose a style, review the prompt the manifest produced, and send the capture
 * to OpenAI.
 *
 * The prompt is deliberately shown, not hidden. It is the whole mechanism by which the model learns
 * that a given silhouette is a red maple in October, so making it visible and editable turns an
 * opaque "make it pretty" button into something inspectable — and lets a user add art direction
 * without losing the botanical grounding underneath.
 */

export interface EnhancePanelDeps {
  apiKeys: ApiKeyStore;
  /** Persist a finished render against its source shot. */
  onSaveVariant: (shot: StoredShot, variant: NewVariant) => Promise<void>;
  onClose: () => void;
  /**
   * The site's semantic tags, resolved per call, for the reference photographs attached to them.
   *
   * A shot is a frame of a site that has since kept editing, so the tags are read now rather than
   * captured with the shot: re-rendering last week's frame should follow the picture of the fence
   * the designer attached yesterday.
   */
  tags?: () => readonly SemanticTag[];
  /**
   * The plan's export ceiling, applied to the Download button beside a finished render.
   *
   * Only to the download. The render itself is made at the size the shot was captured at, on every
   * plan, and is charged the same credits — see `ExportQuality`. Omitted when there is no account.
   */
  exportQuality?: () => ExportQuality | null;
}

export class EnhancePanel {
  readonly root: HTMLElement;

  private shot: StoredShot | null = null;
  private presetId = DEFAULT_STYLE_PRESET_ID;
  private quality: RenderQualityTier = 'medium';
  private variations = 1;
  private promptEdited = false;
  private running = false;
  private controller: AbortController | null = null;
  /** Object URLs created for previews, revoked on teardown to avoid leaking blobs. */
  private readonly objectUrls: string[] = [];

  private readonly promptField: HTMLTextAreaElement;
  private readonly directionField: HTMLInputElement;
  private readonly keyField: HTMLInputElement;
  private readonly styleList: HTMLElement;
  private readonly resultArea: HTMLElement;
  private readonly statusLine: HTMLElement;
  private readonly costLine: HTMLElement;
  private readonly keyRow: HTMLElement;
  private readonly renderButton: HTMLButtonElement;
  private readonly qualityRow: HTMLElement;
  private readonly variationRow: HTMLElement;

  constructor(private readonly deps: EnhancePanelDeps) {
    this.styleList = el('div', { class: 'lg-enhance__styles' });

    this.promptField = el('textarea', {
      class: 'lg-enhance__prompt',
      attrs: { rows: '10', spellcheck: 'false', 'aria-label': 'Render prompt' },
      on: { input: () => { this.promptEdited = true; } },
    });

    this.directionField = el('input', {
      class: 'lg-enhance__input',
      attrs: {
        type: 'text',
        placeholder: 'Optional art direction — e.g. "add a flagstone path"',
        'aria-label': 'Additional art direction',
      },
      on: { input: () => this.regeneratePrompt() },
    });

    this.keyField = el('input', {
      class: 'lg-enhance__input',
      attrs: { type: 'password', placeholder: 'sk-…', 'aria-label': 'OpenAI API key' },
    });

    this.keyRow = el('div', { class: 'lg-enhance__key' });
    this.qualityRow = el('div', { class: 'lg-seg' });
    this.variationRow = el('div', { class: 'lg-seg' });
    this.costLine = el('p', { class: 'lg-enhance__cost' });
    this.statusLine = el('p', { class: 'lg-enhance__status' });
    this.resultArea = el('div', { class: 'lg-enhance__results' });

    this.renderButton = el('button', {
      class: 'lg-btn lg-btn--primary',
      attrs: { type: 'button' },
      html: `${icon('sparkle', 16)}<span>Enhance render</span>`,
      on: { click: () => void this.run() },
    });

    this.buildStyleList();
    this.buildQualityRow();
    this.buildVariationRow();
    this.buildKeyRow();

    this.root = el(
      'aside',
      { class: 'lg-enhance', attrs: { 'aria-label': 'Enhance render' } },
      el(
        'header',
        { class: 'lg-enhance__head' },
        el('h2', { class: 'lg-enhance__title', text: 'Enhance render' }),
        el('button', {
          class: 'lg-iconbtn',
          html: icon('close', 16),
          title: 'Close',
          attrs: { type: 'button', 'aria-label': 'Close enhance panel' },
          on: { click: () => this.deps.onClose() },
        }),
      ),
      el(
        'div',
        { class: 'lg-enhance__body' },
        // First, not last: without a key the Render button is disabled, and burying the reason
        // below a scrolling prompt field makes the panel look broken.
        this.keyRow,
        section('Style', this.styleList),
        section('Quality', this.qualityRow),
        section('Variations', this.variationRow),
        section('Art direction', this.directionField),
        section(
          'Prompt sent with the capture',
          el(
            'div',
            { class: 'lg-enhance__promptwrap' },
            this.promptField,
            el(
              'div',
              { class: 'lg-enhance__promptfoot' },
              el('span', {
                class: 'lg-enhance__hint',
                text: 'Built from the plants actually in frame. Edit freely.',
              }),
              el('button', {
                class: 'lg-btn lg-btn--ghost',
                attrs: { type: 'button' },
                text: 'Reset',
                on: {
                  click: () => {
                    this.promptEdited = false;
                    this.regeneratePrompt();
                  },
                },
              }),
            ),
          ),
        ),
        this.resultArea,
      ),
      el('footer', { class: 'lg-enhance__foot' }, this.costLine, this.statusLine, this.renderButton),
    );
  }

  /** Point the panel at a shot and rebuild its prompt. */
  setShot(shot: StoredShot | null): void {
    this.shot = shot;
    this.promptEdited = false;
    this.clearResults();
    // A failure message from the previous shot must not follow this one around.
    this.setStatus('', 'busy');
    this.regeneratePrompt();
    this.refresh();
  }

  get activeShotId(): string | null {
    return this.shot?.id ?? null;
  }

  private buildStyleList(): void {
    for (const preset of STYLE_PRESETS) {
      this.styleList.appendChild(
        el(
          'button',
          {
            class: 'lg-style',
            attrs: { type: 'button', 'data-preset': preset.id },
            title: preset.blurb,
            on: {
              click: () => {
                this.presetId = preset.id;
                this.regeneratePrompt();
                this.refresh();
              },
            },
          },
          el('span', { class: 'lg-style__label', text: preset.label }),
          el('span', { class: 'lg-style__blurb', text: preset.blurb }),
        ),
      );
    }
  }

  private buildQualityRow(): void {
    for (const tier of RENDER_QUALITY_TIERS) {
      this.qualityRow.appendChild(
        el('button', {
          class: 'lg-seg__btn',
          text: tier,
          attrs: { type: 'button', 'data-quality': tier },
          title:
            tier === 'low'
              ? 'Fastest and cheapest — good for checking framing'
              : tier === 'medium'
                ? 'Balanced quality and cost'
                : 'Best detail, highest cost',
          on: {
            click: () => {
              this.quality = tier;
              this.refresh();
            },
          },
        }),
      );
    }
  }

  private buildVariationRow(): void {
    for (const count of [1, 2, 4]) {
      this.variationRow.appendChild(
        el('button', {
          class: 'lg-seg__btn',
          text: String(count),
          attrs: { type: 'button', 'data-variations': String(count) },
          title: `Generate ${count} variation${count > 1 ? 's' : ''}`,
          on: {
            click: () => {
              this.variations = count;
              this.refresh();
            },
          },
        }),
      );
    }
  }

  private buildKeyRow(): void {
    this.keyRow.appendChild(
      el(
        'div',
        { class: 'lg-enhance__keyinner' },
        el('span', { class: 'lg-enhance__keyicon', html: icon('key', 15) }),
        el('div', { class: 'lg-enhance__keytext' }),
        this.keyField,
        el('button', {
          class: 'lg-btn',
          attrs: { type: 'button' },
          text: 'Save key',
          on: { click: () => this.saveKey() },
        }),
        el('button', {
          class: 'lg-btn lg-btn--ghost',
          attrs: { type: 'button' },
          text: 'Forget',
          on: {
            click: () => {
              this.deps.apiKeys.clear();
              this.refresh();
            },
          },
        }),
      ),
    );
  }

  private saveKey(): void {
    const value = this.keyField.value.trim();
    if (value.length > 0 && !looksLikeApiKey(value)) {
      this.setStatus('That does not look like an OpenAI key — they start with "sk-".', 'error');
      return;
    }
    this.deps.apiKeys.set(value);
    this.keyField.value = '';
    this.setStatus(value.length > 0 ? 'Key saved on this machine.' : 'Key cleared.', 'ok');
    this.refresh();
  }

  private regeneratePrompt(): void {
    if (!this.shot) {
      this.promptField.value = '';
      return;
    }
    // A hand-edited prompt is the user's, not ours — never overwrite it behind their back.
    if (this.promptEdited) return;

    const direction = this.directionField.value.trim();
    const references = this.taggedReferences().references;
    const { text } = buildEnhancePrompt(this.shot.manifest, this.preset(), {
      ...(direction ? { extraDirection: direction } : {}),
      ...(references.length > 0 ? { references } : {}),
    });
    this.promptField.value = text;
  }

  /**
   * Reference photographs off the site's tags that belong with this shot.
   *
   * Resolved against the shot's own manifest, so a picture only travels when the thing it describes
   * is actually in this frame — and recomputed at prompt time as well as at send time, because the
   * prompt shown in the panel has to be the prompt that gets sent.
   */
  private taggedReferences(): ReturnType<typeof collectRenderReferences> {
    if (!this.shot) return { references: [], images: [], skipped: [] };
    return collectRenderReferences(this.shot.manifest, this.deps.tags?.() ?? []);
  }

  private preset(): StylePreset {
    return getStylePreset(this.presetId);
  }

  refresh(): void {
    for (const button of this.styleList.querySelectorAll<HTMLElement>('[data-preset]')) {
      setClass(button, 'is-active', button.dataset.preset === this.presetId);
    }
    for (const button of this.qualityRow.querySelectorAll<HTMLElement>('[data-quality]')) {
      setClass(button, 'is-active', button.dataset.quality === this.quality);
    }
    for (const button of this.variationRow.querySelectorAll<HTMLElement>('[data-variations]')) {
      setClass(button, 'is-active', button.dataset.variations === String(this.variations));
    }

    const hasKey = this.deps.apiKeys.has();
    setClass(this.keyRow, 'is-set', hasKey);
    const keyText = this.keyRow.querySelector('.lg-enhance__keytext');
    if (keyText) {
      keyText.textContent = hasKey
        ? `Key stored (${this.deps.apiKeys.masked() ?? ''})`
        : 'Paste an OpenAI API key to enable rendering.';
    }

    const size = this.shot?.size ?? { width: 1536, height: 1024 };
    const cost = estimateCostUsd(size, this.quality, this.variations);
    this.costLine.textContent = `${ENHANCE_MODEL} · ${size.width}×${size.height} · about ${formatCostUsd(cost)}`;

    this.renderButton.disabled = this.running || !hasKey || this.shot === null;
    const label = this.renderButton.querySelector('span');
    if (label) label.textContent = this.running ? 'Rendering…' : 'Enhance render';

    // Say why the button is dead rather than leaving the user to guess.
    if (!this.running && this.statusLine.dataset.tone !== 'error' && this.statusLine.dataset.tone !== 'ok') {
      this.setStatus(hasKey ? '' : 'Add an OpenAI API key above to enable rendering.', 'busy');
    }
  }

  private async run(): Promise<void> {
    const shot = this.shot;
    const apiKey = this.deps.apiKeys.get();
    if (!shot || apiKey === null || this.running) return;

    this.running = true;
    this.controller = new AbortController();
    this.clearResults();
    this.setStatus('Sending capture and plant key to OpenAI…', 'busy');
    this.refresh();
    this.showCancel();

    const preset = this.preset();
    try {
      // Pull the bytes for the three passes. Local shots resolve immediately; an account's are
      // fetched once and cached by the store, so re-rendering the same shot does not re-download.
      const images = await Promise.all([
        shot.beauty.blob(),
        shot.idMap.blob(),
        shot.depth.blob(),
      ]);

      // Appended after the three passes, in the order the prompt's legend numbers them.
      const tagged = this.taggedReferences()
        .images.map((image) => tagImageToBlob(image))
        .filter((blob): blob is Blob => blob !== null);

      const result = await enhanceRender({
        apiKey,
        images: [...images, ...tagged],
        prompt: this.promptField.value,
        size: shot.size,
        quality: this.quality,
        count: this.variations,
        signal: this.controller.signal,
      });

      this.setStatus(
        `Rendered ${result.images.length} image${result.images.length > 1 ? 's' : ''}.`,
        'ok',
      );
      this.showResults(shot, result.images, preset);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        this.setStatus('Render cancelled.', 'ok');
      } else if (error instanceof EnhanceError) {
        this.setStatus(error.message, 'error');
      } else {
        this.setStatus(error instanceof Error ? error.message : 'The render failed.', 'error');
      }
    } finally {
      this.running = false;
      this.controller = null;
      this.hideCancel();
      this.refresh();
    }
  }

  private showCancel(): void {
    if (this.root.querySelector('.lg-enhance__cancel')) return;
    this.renderButton.insertAdjacentElement(
      'beforebegin',
      el('button', {
        class: 'lg-btn lg-btn--ghost lg-enhance__cancel',
        attrs: { type: 'button' },
        text: 'Cancel',
        on: { click: () => this.controller?.abort() },
      }),
    );
  }

  private hideCancel(): void {
    this.root.querySelector('.lg-enhance__cancel')?.remove();
  }

  private showResults(shot: StoredShot, images: Blob[], preset: StylePreset): void {
    clear(this.resultArea);
    const beforeUrl = shot.beauty.url;

    for (const image of images) {
      const afterUrl = this.trackUrl(URL.createObjectURL(image));
      const card = el(
        'div',
        { class: 'lg-result' },
        compareSlider(beforeUrl, afterUrl),
        el(
          'div',
          { class: 'lg-result__actions' },
          el('button', {
            class: 'lg-btn lg-btn--primary',
            attrs: { type: 'button' },
            text: 'Keep',
            on: {
              click: (event) => {
                const button = event.currentTarget as HTMLButtonElement;
                button.disabled = true;
                button.textContent = 'Saved';
                void this.deps.onSaveVariant(shot, {
                  presetId: preset.id,
                  presetLabel: preset.label,
                  prompt: this.promptField.value,
                  model: ENHANCE_MODEL,
                  quality: this.quality,
                  size: shot.size,
                  image,
                  estimatedCostUsd: estimateCostUsd(shot.size, this.quality, 1),
                });
              },
            },
          }),
          this.downloadButton(
            image,
            afterUrl,
            shot.size,
            `${shot.title.replace(/[^\w-]+/g, '-')}-${preset.id}.png`,
          ),
        ),
      );
      this.resultArea.appendChild(card);
    }
  }

  /**
   * Download, in the two forms phase 6.5 leaves it in.
   *
   * An anchor straight at the render when the plan's ceiling would not change it — the render is
   * already in hand and there is nothing to do. A button when it would, because the resized file
   * does not exist until somebody asks for it, and because that is the case that owes the user a
   * sentence naming the size they are about to get. See `PhotoGallery.downloadControl`, which
   * makes the same split for the same reason.
   */
  private downloadButton(
    image: Blob,
    url: string,
    size: ImageSize,
    filename: string,
  ): HTMLElement {
    const quality = this.deps.exportQuality?.() ?? null;
    const html = `${icon('download', 15)}<span>Download</span>`;
    if (quality === null || !isCapped(size, quality)) {
      return el('a', { class: 'lg-btn', attrs: { href: url, download: filename }, html });
    }

    return el('button', {
      class: 'lg-btn',
      html,
      title: describeExport(size, quality),
      attrs: { type: 'button' },
      on: {
        click: (event) => {
          const button = event.currentTarget as HTMLButtonElement;
          button.disabled = true;
          void toExportBlob(image, size, quality)
            .then((blob) => {
              const href = this.trackUrl(URL.createObjectURL(blob));
              const anchor = el('a', {
                attrs: { href, download: filename },
                style: { display: 'none' },
              });
              this.resultArea.appendChild(anchor);
              anchor.click();
              anchor.remove();
            })
            .finally(() => {
              button.disabled = false;
            });
        },
      },
    });
  }

  private clearResults(): void {
    clear(this.resultArea);
    this.revokeUrls();
  }

  private trackUrl(url: string): string {
    this.objectUrls.push(url);
    return url;
  }

  private revokeUrls(): void {
    for (const url of this.objectUrls) URL.revokeObjectURL(url);
    this.objectUrls.length = 0;
  }

  private setStatus(message: string, tone: 'ok' | 'error' | 'busy'): void {
    this.statusLine.textContent = message;
    this.statusLine.dataset.tone = tone;
  }

  dispose(): void {
    this.controller?.abort();
    this.revokeUrls();
    this.root.remove();
  }
}

function section(label: string, ...children: (Node | string)[]): HTMLElement {
  return el(
    'section',
    { class: 'lg-enhance__section' },
    el('h3', { class: 'lg-enhance__label', text: label }),
    ...children,
  );
}

export interface CompareSliderOptions {
  /** Caption on the left half. Defaults to "Original". */
  beforeLabel?: string;
  /** Caption on the right half. Defaults to "Enhanced". */
  afterLabel?: string;
}

/**
 * Before/after slider. The original sits underneath at full width and the enhanced version is
 * revealed by a clip — so both images stay in identical screen positions and any composition drift
 * the model introduced is immediately visible rather than flattering.
 *
 * The divider carries a grip: two chevrons back to back, in the middle of the line. Without it the
 * control is a white hairline over a photograph and reads as part of the picture — several people
 * looked at a render and never discovered the other half of it was there. The grip says *this moves*
 * before anyone has to guess, and the `is-dragging` class puts the resize cursor on the whole
 * document while a drag is live, so the pointer keeps saying it after it wanders off the image.
 */
export function compareSlider(
  beforeUrl: string,
  afterUrl: string,
  options: CompareSliderOptions = {},
): HTMLElement {
  const after = el('div', {
    class: 'lg-compare__after',
    style: { backgroundImage: `url("${afterUrl}")`, clipPath: 'inset(0 0 0 50%)' },
  });
  const handle = el(
    'div',
    { class: 'lg-compare__handle', style: { left: '50%' } },
    el('span', { class: 'lg-compare__grip', html: icon('dragx', 15) }),
  );

  const range = el('input', {
    class: 'lg-compare__range',
    attrs: { type: 'range', min: '0', max: '100', value: '50', 'aria-label': 'Compare before and after' },
    on: {
      input: (event) => {
        const value = (event.currentTarget as HTMLInputElement).value;
        after.style.clipPath = `inset(0 0 0 ${value}%)`;
        handle.style.left = `${value}%`;
      },
    },
  });

  const root = el(
    'div',
    { class: 'lg-compare' },
    el('img', { class: 'lg-compare__before', attrs: { src: beforeUrl, alt: 'Original render' } }),
    after,
    handle,
    range,
    el('span', {
      class: 'lg-compare__tag lg-compare__tag--left',
      text: options.beforeLabel ?? 'Original',
    }),
    el('span', {
      class: 'lg-compare__tag lg-compare__tag--right',
      text: options.afterLabel ?? 'Enhanced',
    }),
  );

  // Pointer events rather than mouse: the same listeners cover a stylus and a finger, and the
  // matching `pointerup` is guaranteed even when the release lands outside the window.
  const endDrag = (): void => {
    root.classList.remove('is-dragging');
    document.body.classList.remove('lg-resizing-x');
  };
  range.addEventListener('pointerdown', () => {
    root.classList.add('is-dragging');
    document.body.classList.add('lg-resizing-x');
    window.addEventListener('pointerup', endDrag, { once: true });
    window.addEventListener('pointercancel', endDrag, { once: true });
  });

  return root;
}
